// useInventoryStore — single source of truth for the /inventory page.
// Fetches /api/inventory/config, exposes CRUD actions, and remembers
// which outreach tab the admin is focused on (localStorage-persisted).

import { useCallback, useEffect, useState } from "react";
import { API, inv_get, inv_post } from "./constants";
import {
  SEED_CATALOGUE, SEED_HQ_STOCK, SEED_LAST_MONTH_START, SEED_RECEIPTS,
  SEED_REQUESTS, SEED_SUPPLIERS, SEED_TEAMS, SEED_TEAM_STOCK,
} from "./seed";

export function useInventoryStore() {
  const [data, setData] = useState({
    teams: SEED_TEAMS,
    suppliers: SEED_SUPPLIERS,
    catalogue: SEED_CATALOGUE,
    hqStock: SEED_HQ_STOCK,
    teamStock: SEED_TEAM_STOCK,
    requests: SEED_REQUESTS,
    receipts: SEED_RECEIPTS,
    cycles: [],
    pos: [],
    adjustments: [],
    lastMonthStart: SEED_LAST_MONTH_START,
  });
  const [loaded, setLoaded] = useState(false);
  // Which outreach team the admin is currently focused on. Persisted to
  // localStorage so a page reload keeps the selected tab. Used to scope
  // Pending Requests, Transfer Log and Recently Approved slips down to just
  // that team's data (HQ-level views like Purchase Orders stay shared).
  const [activeTeam, setActiveTeamState] = useState(
    typeof window !== "undefined" ? (localStorage.getItem("pp_active_team") || "outreach1") : "outreach1"
  );
  const setActiveTeam = useCallback((t) => {
    setActiveTeamState(t);
    try { localStorage.setItem("pp_active_team", t); } catch { /* storage disabled */ }
  }, []);
  // Auth token — prefer the shared consultations session (also used by
  // /outreach and /pharmacy/partners) so a user who unlocked the
  // inventory gate is automatically admin here too. Fall back to the
  // legacy `pp_token` (localStorage) for the standalone AdminSignInStrip
  // flow that ran before /inventory was gated end-to-end.
  const readToken = () => {
    if (typeof window === "undefined") return "";
    return (
      sessionStorage.getItem("pp_auth_token") ||
      localStorage.getItem("pp_token") ||
      ""
    );
  };
  const [token, setToken] = useState(readToken());

  const refresh = useCallback(async () => {
    try {
      const cfg = await inv_get("/config");
      setData((d) => ({
        ...d,
        teams:     cfg.teams     || d.teams,
        suppliers: cfg.suppliers || d.suppliers,
        catalogue: cfg.items     || d.catalogue,
        hqStock:   cfg.hqStock   || d.hqStock,
        teamStock: cfg.teamStock || d.teamStock,
        requests:  cfg.requests  || d.requests,
        receipts:  cfg.receipts  || d.receipts,
        cycles:    cfg.cycles    || d.cycles,
        pos:       cfg.pos       || d.pos,
        adjustments: cfg.adjustments || d.adjustments,
      }));
      setLoaded(true);
    } catch (e) {
      // Fall back to seed data silently — page still shows the mockup layout.
      console.warn("inventory /config load failed:", e.message);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Re-read the auth token any time either store may have changed
  // (cross-tab storage events + the initial mount).
  useEffect(() => {
    const sync = () => setToken(readToken());
    window.addEventListener("storage", sync);
    // Also poll once shortly after mount so the AuthGate-set session token
    // is picked up even without a storage event (sessionStorage doesn't
    // dispatch a "storage" event in the same tab).
    const t = setTimeout(sync, 100);
    return () => { window.removeEventListener("storage", sync); clearTimeout(t); };
  }, []);

  // Admin-tier resolution — the gate lets staff in with EITHER the
  // view-only `inventory` permission or full `inventory_admin`. Both
  // hold a valid session token, so a bare Boolean(token) can't drive
  // the admin UI (staff would see admin sections that all 403). Ask
  // the backend whether this token actually carries inventory_admin.
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!token) { setIsAdmin(false); return undefined; }
    fetch(`${API}/inventory/whoami`, { headers: { "X-Auth-Token": token } })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!cancelled) setIsAdmin(Boolean(j && j.admin)); })
      .catch(() => { if (!cancelled) setIsAdmin(false); });
    return () => { cancelled = true; };
  }, [token]);

  const submitRequest = useCallback(async ({ team, item, qty, reason, submittedBy }) => {
    await inv_post("/requests", { team, item, qty, reason: reason || "", submittedBy: submittedBy || "" }, token);
    await refresh();
  }, [token, refresh]);

  const createTransfer = useCallback(async ({ team, item, qty, reason, movedBy }) => {
    const res = await inv_post("/transfers", {
      team, item, qty,
      reason: reason || "",
      movedBy: movedBy || "",
    }, token);
    await refresh();
    return res;
  }, [token, refresh]);

  const approveRequest = useCallback(async (rid) => {
    const approved = await inv_post(`/requests/${rid}/approve`, {}, token);
    await refresh();
    return approved;
  }, [token, refresh]);

  const denyRequest = useCallback(async (rid) => {
    await inv_post(`/requests/${rid}/deny`, {}, token);
    await refresh();
  }, [token, refresh]);

  // Staff-facing "oops, cancel that" — works without an admin token so
  // outreach can withdraw their own pending row directly from the count
  // screen. Backend rejects it (400) once admin has approved / denied.
  const cancelRequest = useCallback(async (rid) => {
    await inv_post(`/requests/${rid}/cancel`, {});
    await refresh();
  }, [refresh]);

  const logReceipt = useCallback(async ({ supplier, item, qty, po, receivedBy }) => {
    await inv_post("/receipts", { supplier, item, qty, po: po || "", receivedBy: receivedBy || "" }, token);
    await refresh();
  }, [token, refresh]);

  const approvePO = useCallback(async ({ supplier, items, approvedBy, note, status }) => {
    const res = await inv_post("/pos", {
      supplier,
      items,
      approvedBy: approvedBy || "",
      note: note || "",
      status: status || "approved",
    }, token);
    await refresh();
    return res;
  }, [token, refresh]);

  const updatePO = useCallback(async (pid, patch) => {
    const r = await fetch(`${API}/inventory/pos/${pid}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { "X-Auth-Token": token } : {}),
      },
      body: JSON.stringify(patch || {}),
    });
    if (!r.ok) {
      const raw = await r.text();
      let msg = "";
      try { const j = JSON.parse(raw); msg = typeof j?.detail === "string" ? j.detail : ""; } catch (_) { /* not JSON */ }
      throw new Error(msg || raw || `PATCH /pos/${pid} ${r.status}`);
    }
    const res = await r.json();
    await refresh();
    return res;
  }, [token, refresh]);

  const receivePO = useCallback(async (pid) => {
    const res = await inv_post(`/pos/${pid}/receive`, {}, token);
    await refresh();
    return res;
  }, [token, refresh]);

  const deletePO = useCallback(async (pid) => {
    const r = await fetch(`${API}/inventory/pos/${pid}`, {
      method: "DELETE",
      headers: { ...(token ? { "X-Auth-Token": token } : {}) },
    });
    if (!r.ok) {
      const raw = await r.text();
      let msg = "";
      try { const j = JSON.parse(raw); msg = typeof j?.detail === "string" ? j.detail : ""; } catch (_) { /* not JSON */ }
      throw new Error(msg || raw || `DELETE /pos/${pid} ${r.status}`);
    }
    await refresh();
    return r.json();
  }, [token, refresh]);

  const adjustStock = useCallback(async ({ scope, item, new_qty, reason, team: teamId }) => {
    const res = await inv_post("/adjustments", {
      scope,
      item,
      new_qty: Number(new_qty),
      reason: reason || "",
      team: teamId || null,
    }, token);
    await refresh();
    return res;
  }, [token, refresh]);

  const hqPurchase = useCallback(async ({ item, packs, note, create_po = true }) => {
    const res = await inv_post("/hq/purchase", {
      item,
      packs: Number(packs),
      note: note || "",
      create_po: create_po,
    }, token);
    await refresh();
    return res;
  }, [token, refresh]);

  const submitCycle = useCallback(async ({ team, month, phase, items, submittedBy, autoOrder = true, snapStock = true }) => {
    await inv_post("/cycles", {
      team, month, phase, items,
      submittedBy: submittedBy || "",
      auto_order: autoOrder,
      snap_stock: snapStock,
    }, token);
    await refresh();
  }, [token, refresh]);

  const createSupplier = useCallback(async (body) => {
    const res = await inv_post("/suppliers", body, token);
    await refresh();
    return res;
  }, [token, refresh]);

  const updateSupplier = useCallback(async (sid, body) => {
    const r = await fetch(`${API}/inventory/suppliers/${sid}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { "X-Auth-Token": token } : {}),
      },
      body: JSON.stringify(body || {}),
    });
    if (!r.ok) {
      const raw = await r.text();
      let msg = "";
      try { const j = JSON.parse(raw); msg = typeof j?.detail === "string" ? j.detail : ""; } catch (_) { /* not JSON */ }
      throw new Error(msg || raw || `PATCH /suppliers/${sid} ${r.status}`);
    }
    const res = await r.json();
    await refresh();
    return res;
  }, [token, refresh]);

  const createItem = useCallback(async (body) => {
    const res = await inv_post("/items", body, token);
    await refresh();
    return res;
  }, [token, refresh]);

  const updateItem = useCallback(async (iid, body) => {
    const r = await fetch(`${API}/inventory/items/${iid}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { "X-Auth-Token": token } : {}),
      },
      body: JSON.stringify(body || {}),
    });
    if (!r.ok) {
      const raw = await r.text();
      let msg = "";
      try { const j = JSON.parse(raw); msg = typeof j?.detail === "string" ? j.detail : ""; } catch (_) { /* not JSON */ }
      throw new Error(msg || raw || `PATCH /items/${iid} ${r.status}`);
    }
    const res = await r.json();
    await refresh();
    return res;
  }, [token, refresh]);

  const deleteSupplier = useCallback(async (sid) => {
    const r = await fetch(`${API}/inventory/suppliers/${sid}`, {
      method: "DELETE",
      headers: token ? { "X-Auth-Token": token } : {},
    });
    if (!r.ok) throw new Error(await r.text());
    await refresh();
  }, [token, refresh]);

  const deleteItem = useCallback(async (iid) => {
    const r = await fetch(`${API}/inventory/items/${iid}`, {
      method: "DELETE",
      headers: token ? { "X-Auth-Token": token } : {},
    });
    if (!r.ok) throw new Error(await r.text());
    await refresh();
  }, [token, refresh]);

  const clearAllData = useCallback(async () => {
    const res = await inv_post("/admin/clear", {}, token);
    await refresh();
    return res;
  }, [token, refresh]);

  return {
    ...data,
    loaded,
    activeTeam,
    setActiveTeam,
    // `hasToken` means "holds an inventory_ADMIN-capable token" — it
    // gates every admin surface (HQ receiving, transfers, suppliers,
    // approve/deny). Staff with the view-only `inventory` permission
    // keep hasToken=false and only see the self-serve sections.
    hasToken: isAdmin,
    token,
    setToken,
    refresh,
    submitRequest,
    createTransfer,
    approveRequest,
    denyRequest,
    cancelRequest,
    logReceipt,
    submitCycle,
    approvePO,
    updatePO,
    receivePO,
    deletePO,
    adjustStock,
    hqPurchase,
    createSupplier,
    updateSupplier,
    createItem,
    updateItem,
    deleteSupplier,
    deleteItem,
    clearAllData,
  };
}
